<script setup lang="ts">
import { onBeforeUnmount, ref } from 'vue'
import { NPopover } from 'naive-ui'
import { CITATION_MARKER_ANCHOR_SELECTOR, parseCitationMarker } from '../composables/useWikiMarkdown.citations'
import CitationHoverCard from './CitationHoverCard.vue'

// Wraps a slot of rendered markdown that contains inline `[n]`
// citation markers (the `<sup class="wiki-citation-marker">` shape
// emitted by useWikiMarkdown.citations) and shows a programmatic
// `NPopover` with a snippet preview when the user hovers or focuses a
// marker. One manager covers the whole slot — events are delegated at
// the container level so adding more bubbles to ThreadDrawer doesn't
// multiply listeners.
//
// Positioning: NPopover with `trigger="manual"` and `:x`/`:y` props
// snaps to absolute viewport coordinates. We pass the centre of the
// marker's bounding rect so the popover anchors above (default
// placement) and tracks the marker even if the page scrolls between
// hovers.
//
// Dismiss flow: mouseleave on the marker schedules a 200 ms close so
// the user has time to move the cursor into the popover content; the
// popover wrapper's own mouseenter cancels the timer. Escape closes
// immediately. Focus-driven hovers (keyboard tab onto a marker) work
// via the same active-marker state — the close-on-blur path mirrors
// mouseleave.

interface ActiveMarker {
  x: number
  y: number
  path: string
  index: number
}

// 200 ms — wide enough that a steady cursor movement from marker to
// popover content keeps the popover open, narrow enough that an
// accidental glance doesn't trap the popover open after the user
// moves on.
const POPOVER_GRACE_MS = 200

const active = ref<ActiveMarker | null>(null)
let closeTimer: number | undefined

function showFor(target: HTMLElement, parsed: { path: string; index: number }): void {
  const rect = target.getBoundingClientRect()
  cancelClose()
  active.value = {
    x: rect.left + rect.width / 2,
    y: rect.top,
    path: parsed.path,
    index: parsed.index,
  }
}

// Resolve the closest marker anchor to use for both rect lookup AND
// data parsing. Returns the anchor element + parsed data when the
// event landed on a marker; null otherwise. The anchor lookup mirrors
// `parseCitationMarker`'s closest() call so we get a single, consistent
// notion of "what's the marker for this event".
function resolveMarker(event: Event): { anchor: HTMLElement; data: { path: string; index: number } } | null {
  const data = parseCitationMarker(event.target)
  if (!data) return null
  const anchor = (event.target as HTMLElement).closest<HTMLElement>(CITATION_MARKER_ANCHOR_SELECTOR)
  if (!anchor) return null
  return { anchor, data }
}

function onPointerOver(event: MouseEvent): void {
  const hit = resolveMarker(event)
  if (!hit) return
  showFor(hit.anchor, hit.data)
}

function onPointerOut(event: MouseEvent): void {
  if (!parseCitationMarker(event.target)) return
  // Schedule close — the popover's own mouseenter cancels the timer
  // when the user is moving INTO the card.
  scheduleClose()
}

function onFocusIn(event: FocusEvent): void {
  const hit = resolveMarker(event)
  if (!hit) return
  showFor(hit.anchor, hit.data)
}

function onFocusOut(event: FocusEvent): void {
  if (!parseCitationMarker(event.target)) return
  scheduleClose()
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && active.value) {
    cancelClose()
    active.value = null
  }
}

function scheduleClose(): void {
  cancelClose()
  closeTimer = window.setTimeout(() => {
    active.value = null
    closeTimer = undefined
  }, POPOVER_GRACE_MS)
}

function cancelClose(): void {
  if (closeTimer !== undefined) {
    window.clearTimeout(closeTimer)
    closeTimer = undefined
  }
}

onBeforeUnmount(cancelClose)
</script>

<template>
  <div
    class="contents"
    @mouseover="onPointerOver"
    @mouseout="onPointerOut"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
    @keydown="onKeydown"
  >
    <slot />
  </div>

  <NPopover
    :show="!!active"
    :x="active?.x ?? 0"
    :y="active?.y ?? 0"
    trigger="manual"
    placement="top"
    :show-arrow="true"
    raw
    data-testid="citation-popover"
  >
    <div
      class="rounded-xl border border-border-subtle bg-surface-raised p-3 shadow-xl"
      @mouseenter="cancelClose"
      @mouseleave="scheduleClose"
    >
      <CitationHoverCard v-if="active" :path="active.path" :index="active.index" />
    </div>
  </NPopover>
</template>
